import React, { useState } from "react";
import { Mail, Phone, MapPin, Clock, Send, Sparkles } from "lucide-react";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Button } from "../ui/button";
import { useToast } from "../../hooks/use-toast";
import { contactInfo } from "../../data/mock";
import { postContact } from "../../lib/api";
import { useLanguage } from "../../i18n/LanguageContext";

export default function Contact() {
  const { toast } = useToast();
  const { t } = useLanguage();
  const [form, setForm] = useState({ name: "", email: "", company: "", message: "" });
  const [sending, setSending] = useState(false);
  const hours = t("contact.hours") || [];

  const handle = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }));
  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast({ title: t("contact.missingTitle"), description: t("contact.missingDesc") });
      return;
    }
    setSending(true);
    try {
      await postContact(form);
      toast({ title: t("contact.successTitle"), description: `${t("contact.successDescPre")} ${form.name}${t("contact.successDescPost")}` });
      setForm({ name: "", email: "", company: "", message: "" });
    } catch (err) {
      const detail = err?.response?.data?.detail || "";
      toast({ title: t("contact.failTitle"), description: String(detail) });
    } finally {
      setSending(false);
    }
  };

  const cls = "bg-white/[0.03] border-white/10 focus-visible:border-cyan-400/50 focus-visible:ring-cyan-400/20 text-white placeholder:text-slate-500 h-11";

  return (
    <section id="contact" className="relative py-24 lg:py-32 overflow-hidden">
      <div className="absolute inset-0 hero-bg" />
      <div className="absolute inset-0 stars" />

      <div className="relative max-w-7xl mx-auto px-6 lg:px-10">
        <div className="text-center max-w-3xl mx-auto mb-14">
          <span className="kicker">{t("contact.kicker")}</span>
          <h2 className="mt-5 font-display text-4xl lg:text-5xl font-bold text-white">
            {t("contact.title1")} <span className="grad-text">{t("contact.titleAccent")}</span>
          </h2>
          <p className="mt-4 text-slate-300/90 text-lg">{t("contact.description")}</p>
        </div>

        <div className="grid lg:grid-cols-5 gap-6">
          <div className="lg:col-span-2 space-y-5">
            <InfoCard icon={Mail} title={t("contact.emailLabel")}>
              {contactInfo.emails.map((e) => (
                <a key={e} href={`mailto:${e}`} className="block text-slate-300 hover:text-cyan-300 transition text-sm">{e}</a>
              ))}
            </InfoCard>
            <InfoCard icon={Phone} title={t("contact.phoneLabel")}>
              <a href={`tel:${contactInfo.phone.replace(/\s/g, "")}`} className="text-slate-300 hover:text-cyan-300 transition text-lg font-semibold">{contactInfo.phone}</a>
            </InfoCard>
            <InfoCard icon={MapPin} title={t("contact.officeLabel")}>
              {contactInfo.address.map((line) => (
                <div key={line} className="text-slate-300 text-sm">{line}</div>
              ))}
            </InfoCard>
            <InfoCard icon={Clock} title={t("contact.hoursLabel")}>
              <div className="space-y-1 text-sm">
                {hours.map((h) => (
                  <div key={h.day} className="flex justify-between">
                    <span className="text-slate-400">{h.day}</span>
                    <span className="text-slate-200">{h.time}</span>
                  </div>
                ))}
              </div>
              <div className="mt-3 inline-flex items-center gap-2 text-xs text-cyan-300">
                <span className="w-1.5 h-1.5 rounded-full bg-cyan-400 animate-pulse" />
                {t("contact.emergency")}
              </div>
            </InfoCard>
          </div>

          <form onSubmit={submit} className="lg:col-span-3 glass rounded-2xl p-8 lg:p-10">
            <div className="flex items-center gap-2 mb-6">
              <Sparkles className="w-5 h-5 text-cyan-300" />
              <h3 className="font-display text-xl font-semibold text-white">{t("contact.sendMessage")}</h3>
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <Field label={t("contact.name")}>
                <Input name="name" value={form.name} onChange={handle} placeholder={t("contact.namePh")} className={cls} />
              </Field>
              <Field label={t("contact.email")}>
                <Input name="email" type="email" value={form.email} onChange={handle} placeholder={t("contact.emailPh")} className={cls} />
              </Field>
            </div>
            <div className="mt-4">
              <Field label={t("contact.company")}>
                <Input name="company" value={form.company} onChange={handle} placeholder={t("contact.companyPh")} className={cls} />
              </Field>
            </div>
            <div className="mt-4">
              <Field label={t("contact.message")}>
                <Textarea name="message" value={form.message} onChange={handle} rows={6} placeholder={t("contact.messagePh")} className={cls} />
              </Field>
            </div>
            <Button type="submit" disabled={sending} className="mt-6 h-12 px-7 btn-primary rounded-xl font-semibold border-0">
              {sending ? t("contact.sending") : (<><Send className="w-4 h-4 mr-2" /> {t("contact.send")}</>)}
            </Button>
          </form>
        </div>
      </div>
    </section>
  );
}

function InfoCard({ icon: Icon, title, children }) {
  return (
    <div className="glass lift rounded-2xl p-6">
      <div className="flex items-start gap-4">
        <div className="shrink-0 w-11 h-11 rounded-xl bg-gradient-to-br from-sky-500/25 to-cyan-400/25 border border-cyan-400/25 flex items-center justify-center">
          <Icon className="w-5 h-5 text-cyan-300" strokeWidth={2} />
        </div>
        <div className="flex-1">
          <div className="text-[11px] uppercase tracking-widest text-slate-400 font-semibold mb-2">{title}</div>
          {children}
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="block text-[11px] uppercase tracking-widest text-slate-400 font-semibold mb-2">{label}</span>
      {children}
    </label>
  );
}
